import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { updateProfile } from '../../services/api';
import toast from 'react-hot-toast';

export default function Profile() {
  const { user, logout } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [loading, setLoading] = useState(false);

  const getInitials = (n = '') =>
    n.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('name', name);
      await updateProfile(fd);
      toast.success('Profile updated!');
    } catch (err) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.inner}>
        {/* Avatar */}
        <div style={styles.avatarSection}>
          <div style={styles.avatar}>{getInitials(user?.name)}</div>
          <h2 style={styles.name}>{user?.name}</h2>
          <p style={styles.email}>{user?.email}</p>
          {user?.isVip && <span style={styles.vipBadge}>⭐ VIP MEMBER</span>}
        </div>

        {/* Form */}
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>EDIT PROFILE</h3>
          <form onSubmit={handleSave} style={styles.form}>
            <div style={styles.field}>
              <label style={styles.label}>DISPLAY NAME</label>
              <input
                style={styles.input}
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Your name"
              />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>EMAIL</label>
              <div style={styles.inputDisabled}>{user?.email}</div>
            </div>
            <button
              style={{ ...styles.btn, opacity: loading ? 0.6 : 1 }}
              disabled={loading}
            >
              {loading ? 'Saving...' : 'SAVE CHANGES'}
            </button>
          </form>
        </div>

        {/* Account info */}
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>ACCOUNT INFO</h3>
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>Status</span>
            <span style={styles.statusBadge}>✅ Active</span>
          </div>
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>Role</span>
            <span style={styles.roleBadge}>Player</span>
          </div>
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>VIP</span>
            <span style={{ color: user?.isVip ? '#ffd700' : 'rgba(255,255,255,0.3)', fontSize: '14px' }}>
              {user?.isVip ? '⭐ Yes' : 'No'}
            </span>
          </div>
        </div>

        {/* Logout */}
        <button style={styles.logoutBtn} onClick={logout}>
          ⏻ &nbsp;Logout from Blixo
        </button>

        <p style={styles.version}>Blixo v1.0.0 · Fantasy Gaming Support</p>
      </div>
    </div>
  );
}

const styles = {
  container: {
    flex: 1, overflowY: 'auto',
    padding: '28px', fontFamily: "'Exo 2', sans-serif",
  },
  inner: { maxWidth: '520px', margin: '0 auto' },
  avatarSection: {
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', marginBottom: '24px',
  },
  avatar: {
    width: '88px', height: '88px', borderRadius: '22px',
    background: '#003d38', display: 'flex',
    alignItems: 'center', justifyContent: 'center',
    color: '#f5a623', fontSize: '30px', fontWeight: '900',
    border: '2px solid rgba(245,166,35,0.3)', marginBottom: '14px',
    fontFamily: "'Rajdhani', sans-serif",
  },
  name: {
    margin: 0, fontSize: '22px', fontWeight: '700',
    color: '#e2e8f0', fontFamily: "'Rajdhani', sans-serif",
  },
  email: { margin: '4px 0 0', color: 'rgba(255,255,255,0.3)', fontSize: '14px' },
  vipBadge: {
    marginTop: '10px',
    background: 'rgba(255,215,0,0.1)',
    border: '1px solid rgba(255,215,0,0.3)',
    color: '#ffd700', borderRadius: '10px',
    padding: '4px 14px', fontSize: '12px', fontWeight: '700',
    letterSpacing: '2px',
  },
  card: {
    background: '#0d1420', borderRadius: '16px',
    border: '1px solid rgba(245,166,35,0.08)',
    padding: '24px', marginBottom: '16px',
  },
  cardTitle: {
    margin: '0 0 20px', fontSize: '12px', fontWeight: '700',
    color: 'rgba(245,166,35,0.6)', letterSpacing: '3px',
  },
  form: { display: 'flex', flexDirection: 'column', gap: '16px' },
  field: { display: 'flex', flexDirection: 'column', gap: '7px' },
  label: {
    color: 'rgba(245,166,35,0.6)', fontSize: '11px',
    fontWeight: '700', letterSpacing: '2px',
  },
  input: {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(245,166,35,0.15)',
    borderRadius: '10px', padding: '12px 14px',
    color: '#fff', fontSize: '15px',
    fontFamily: "'Exo 2', sans-serif", outline: 'none',
  },
  inputDisabled: {
    background: 'rgba(255,255,255,0.02)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: '10px', padding: '12px 14px',
    color: 'rgba(255,255,255,0.25)', fontSize: '15px',
  },
  btn: {
    background: '#f5a623', border: 'none', borderRadius: '10px',
    padding: '13px', color: '#080c14', fontSize: '14px',
    fontWeight: '900', letterSpacing: '2px', cursor: 'pointer',
    fontFamily: "'Rajdhani', sans-serif",
  },
  infoRow: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', padding: '10px 0',
    borderBottom: '1px solid rgba(255,255,255,0.04)',
  },
  infoLabel: { color: 'rgba(255,255,255,0.35)', fontSize: '14px' },
  statusBadge: {
    background: 'rgba(0,255,100,0.1)',
    border: '1px solid rgba(0,255,100,0.2)',
    color: '#00ff88', borderRadius: '8px',
    padding: '4px 10px', fontSize: '12px',
  },
  roleBadge: {
    background: 'rgba(100,100,255,0.1)',
    border: '1px solid rgba(100,100,255,0.2)',
    color: '#a0a0ff', borderRadius: '8px',
    padding: '4px 10px', fontSize: '12px',
  },
  logoutBtn: {
    width: '100%', background: 'rgba(255,50,50,0.08)',
    border: '1px solid rgba(255,50,50,0.2)',
    borderRadius: '12px', color: '#ff6b6b',
    padding: '14px', fontSize: '15px', cursor: 'pointer',
    fontFamily: "'Exo 2', sans-serif", fontWeight: '600',
    marginBottom: '16px',
  },
  version: { textAlign: 'center', color: 'rgba(255,255,255,0.15)', fontSize: '12px' },
};